import React, { useCallback, useEffect, useState } from 'react';
import { theme } from 'src/themes';
import { AssetClassEnum } from 'src/common/enums/assetClasses';
import { LoadingProgress } from 'src/common/store/types';
import * as yup from 'yup';
import PulseLoader from 'react-spinners/PulseLoader';
import DragHandleIcon from '@mui/icons-material/DragHandle';
import { Add as AddIcon, Cancel as CancelIcon, Delete as DeleteIcon, Edit as EditIcon, Save as SaveIcon } from '@mui/icons-material';
import { Box, IconButton, LinearProgress, Paper, Stack, styled, Typography } from '@mui/material';
import { GridToolbarContainer } from '@mui/x-data-grid';
import {
  DataGridPro,
  GridActionsCellItem,
  GridColumns,
  GridPreProcessEditCellProps,
  GridRenderCellParams,
  GridRowId,
  GridRowModes,
  GridRowParams,
  GridValueGetterParams,
  GridColumnVisibilityModel,
  MuiBaseEvent,
  MuiEvent,
  useGridApiRef,
  gridEditRowsStateSelector,
} from '@mui/x-data-grid-pro';
import { AssetClassOrderItem, UpsertAssetClassOrderPayload } from '../store/types';

const StyledBox = styled(Box)(() => ({
  width: '100%',
  '& .MuiDataGrid-cell--editing': {
    backgroundColor: 'rgb(255,215,115, 0.19)',
    color: '#1a3e72',
    '& .MuiInputBase-root': {
      height: '100%',
    },
  },
  '& .Mui-error': {
    backgroundColor: `#FFCCCC`,
    color: 'red',
    borderRadius: '4px',
    borderColor: 'red',
    borderStyle: 'solid',
    borderWidth: '1px',
  },
  '& .MuiDataGrid-editInputCell': {
    borderWidth: '2px',
  },
  '& input[type=number]': {
    textAlign: 'right',
    paddingRight: '0px',
  },
  '& .MuiDataGrid-toolbarContainer': {
    justifyContent: 'flex-end',
  },
}));

export interface AssetClassOrderAllocationTableProps {
  afslId: number | undefined;
  assetClassOrderItems: AssetClassOrderItem[];
  itemLoadingProgress: LoadingProgress;
  onSave?: (upsertAssetClassOrderPayload: UpsertAssetClassOrderPayload) => Promise<void>;
  savingProgress: LoadingProgress;
  hideEditControls?: boolean;
}

interface DataRow {
  id: string;
  assetClassId: number | null | undefined;
  sortOrder: number;
}

export const ReportSettingsAssetClassTable = (props: AssetClassOrderAllocationTableProps): JSX.Element => {
  const { afslId, assetClassOrderItems, itemLoadingProgress, onSave, savingProgress, hideEditControls } = props;
  const [dataRows, setDataRows] = useState<DataRow[]>([]);
  const [editMode, setEditMode] = useState<boolean>(false);
  const [sortOrderAllocationValidationMessage, setSortOrderAllocationValidationMessage] = useState<string>('');
  const [columnVisibilityModel, setColumnVisibilityModel] = useState<GridColumnVisibilityModel>({
    actions: editMode,
    dragOrder: false, // hidden on load, but visible in Edit mode
    sortOrder: true, // visible on load, but hidden in Edit mode
  });

  const apiRef = useGridApiRef();

  // --> Sort order validations start
  const sortOrderValidationSchema = yup.number().required().min(1).max(99);
  const validationSchema = yup.object().shape({
    sortOrderAllocations: yup
      .array()
      .of(
        yup.object().shape({
          id: yup.string(),
          assetClassId: yup.number().nullable().required('Asset class is required'),
          sortOrder: sortOrderValidationSchema,
        })
      )
      .test('is-sequential', 'Sort order numbers must be sequential, non-negative and without gaps; e.g.: 1,2,3,...', (value) => {
        if (!value || value.length === 0) return false;
        const sortOrders = (value as DataRow[]).map((r) => r.sortOrder); // Extract sortOrder values
        const sorted = [...sortOrders].sort((a, b) => a - b); // Sort them
        const expected = Array.from({ length: sorted.length }, (_, i) => i + 1); // Expected sequence [1..N]
        return sorted.every((num, idx) => num === expected[idx]); // Compare arrays
      }),
  });

  const validateSortOrderAllocations = React.useCallback(() => {
    const sortOrderAllocations: DataRow[] = Object.entries(gridEditRowsStateSelector(apiRef.current.state))
      .map((r) => r[1])
      .map((r, index) => ({
        id: index.toString(),
        assetClassId: r.assetClassId.value !== '' ? r.assetClassId.value : null,
        sortOrder: r.sortOrder.value,
      }));

    validationSchema
      .validate({ sortOrderAllocations: sortOrderAllocations })
      .then(() => {
        setSortOrderAllocationValidationMessage('');
      })
      .catch((e) => {
        setSortOrderAllocationValidationMessage(e.message);
      });
  }, [apiRef.current.state]);

  useEffect(() => {
    resetDataRows();
  }, [assetClassOrderItems]);
  // <-- Sort order validations end

  // --> Main grid handler functions start
  const resetDataRows = useCallback(() => {
    const itemsToProcess = assetClassOrderItems
      .map((i, index) => ({
        id: (index + 1).toString(),
        assetClassId: i.assetClassId,
        sortOrder: i.sortOrder,
      }))
      .filter((i) => i.assetClassId !== AssetClassEnum.Unallocated.id);

    setDataRows([...itemsToProcess]);
  }, [assetClassOrderItems, dataRows]);

  // Save AssetClass handler
  const handleSave = useCallback(async () => {
    if (!!onSave) {
      const editModels = gridEditRowsStateSelector(apiRef.current.state);

      const merged = [...dataRows];
      for (const [id, edit] of Object.entries(editModels)) {
        const existingIndex = merged.findIndex((r) => r.id === id);
        if (existingIndex < 0) {
          const newRow: DataRow = {
            id,
            assetClassId: edit.assetClassId.value,
            sortOrder: edit.sortOrder.value,
          };
          merged.push(newRow);
        }
      }

      handleCancelClick();

      const sorted = applySequentialSortOrder(merged);

      onSave({
        afslId,
        assetClassOrderItems: [
          ...Object.entries(sorted).map((m) => ({
            assetClassId: Number(m[1].assetClassId),
            sortOrder: Number(m[1].sortOrder),
          })),
        ],
      });

      restoreDefaultVisibility();
    }
  }, [onSave, apiRef, afslId, dataRows]);

  // Add new AssetClass handler
  const handleAddClick = React.useCallback(() => {
    const id = (apiRef.current.getAllRowIds().length + 1).toString();

    const newRow: DataRow = {
      id,
      assetClassId: null,
      sortOrder: apiRef.current.getAllRowIds().length + 1, // set the initial new value for sort order +1 from last one
    };
    apiRef.current.updateRows([{ ...newRow, id, isNew: true }]);
    apiRef.current.setRowMode(id, GridRowModes.Edit);
    apiRef.current.setEditCellValue({ id, field: 'assetClassId', value: '' });

    // Wait for the grid to render with the new row
    setTimeout(() => {
      apiRef.current.scrollToIndexes({
        rowIndex: apiRef.current.getRowsCount() - 1,
      });

      apiRef.current.setCellFocus(-1, 'name');
    });
  }, [apiRef.current]);

  // Delete AssetClass handler
  const deleteRow = React.useCallback(
    (id: string) => () => {
      apiRef.current.setRowMode(id, GridRowModes.View);
      apiRef.current.updateRows([{ id, _action: 'delete' }]);

      setDataRows((prev) => prev.filter((row) => row.id !== id));

      apiRef.current.getAllRowIds().forEach((id, index) => {
        apiRef.current.setEditCellValue({ id, field: 'sortOrder', value: (index + 1).toString() });
      });
    },
    [apiRef, dataRows]
  );

  // Edit new AssetClass handler
  const handleEditClick = React.useCallback(() => {
    setColumnVisibilityModel({
      actions: true,
      dragOrder: true, // show drag handle column
      sortOrder: true, // hide sortOrder column
    });

    setEditMode(true);

    apiRef.current.getAllRowIds().forEach((id) => {
      apiRef.current.setRowMode(id, GridRowModes.Edit);
    });
  }, [apiRef]);

  // Cancel new AssetClass handler
  const handleCancelClick = React.useCallback(() => {
    setEditMode(false);
    apiRef.current.getAllRowIds().forEach((id) => {
      const rowMode = apiRef.current.getRowMode(id);
      if (rowMode === 'edit') {
        apiRef.current.setRowMode(id, GridRowModes.View);
      }
      const row = apiRef.current.getRow(id);
      if (!!row && row.isNew) {
        apiRef.current.updateRows([{ id, _action: 'delete' }]);
      }
    });

    resetDataRows();
    restoreDefaultVisibility();
  }, [apiRef, assetClassOrderItems]);
  // --> Main grid handler functions end

  // --> Helper methods section start
  const applySequentialSortOrder = (updated: DataRow[]) => {
    return updated.map((row, index) => ({
      ...row,
      sortOrder: index + 1,
    }));
  };

  const restoreDefaultVisibility = () => {
    setColumnVisibilityModel({
      actions: false,
      dragOrder: false,
      sortOrder: true,
    });
  };

  const reorderRows = React.useCallback(
    (rows, sourceId, targetId) => {
      const startIndex = rows.findIndex((r: { id: number }) => r.id === sourceId);
      const endIndex = rows.findIndex((r: { id: number }) => r.id === targetId);
      if (startIndex === -1 || endIndex === -1) return rows;

      const updated = [...rows];
      const [moved] = updated.splice(startIndex, 1);
      updated.splice(endIndex, 0, moved);

      return applySequentialSortOrder(updated);
    },
    [apiRef]
  );
  // <-- Helper methods section end

  // --> Drag&Drop handlers section start
  const dragRowId = React.useRef<GridRowId | null>(null);

  const handleDragStart = (params: GridRenderCellParams) => {
    dragRowId.current = params.id;
  };

  const handleDragOver = (event: { preventDefault: () => void }) => {
    event.preventDefault();
  };

  const handleDrop = React.useCallback(
    (params: GridRenderCellParams) => {
      const sourceId = dragRowId.current;
      const targetId = params.id;

      if (!sourceId || sourceId === targetId) return;

      const newRows = reorderRows(dataRows, sourceId, targetId);
      setDataRows(newRows);

      apiRef.current.getAllRowIds().forEach((id, index) => {
        apiRef.current.setEditCellValue({ id, field: 'sortOrder', value: (index + 1).toString() });
      });

      dragRowId.current = null;
    },
    [apiRef, dataRows]
  );
  // <-- Drag&Drop handlers section end

  const DragHandleCell = (params: GridRenderCellParams) => (
    <div
      draggable
      onDragStart={() => handleDragStart(params)}
      onDragOver={handleDragOver}
      onDrop={() => handleDrop(params)}
      style={{
        cursor: 'grab',
        paddingLeft: 8,
        paddingRight: 8,
        display: 'flex',
        alignItems: 'center',
        color: '#4306fa',
        flex: 1,
        flexFlow: 'column',
      }}
    >
      <DragHandleIcon fontSize="small" />
    </div>
  );

  const columns: GridColumns = [
    {
      field: 'assetClassId',
      headerName: 'ASSET CLASS NAME',
      type: 'singleSelect',
      flex: 1,
      editable: true,
      sortable: true,
      renderCell: (params: GridRenderCellParams<number>) => {
        return <div>{AssetClassEnum.getById(params.value)?.displayName}</div>;
      },
      valueOptions: (params) => {
        const assetClasseIdsInUse = Object.entries(gridEditRowsStateSelector(apiRef.current.state))
          .filter((r) => r[0] !== params.id)
          .map((r) => r[1].assetClassId.value);

        return [...AssetClassEnum.getAll()]
          .filter((e) => !assetClasseIdsInUse.includes(e.id))
          .map((c) => ({ value: c.id, label: c.displayName }))
          .sort((a, b) => a.label.localeCompare(b.label));
      },
      preProcessEditCellProps: (params: GridPreProcessEditCellProps) => {
        return { ...params.props, error: params.props.value === '' };
      },
      valueGetter: (params: GridValueGetterParams) => {
        return params.value !== null ? params.value : '';
      },
    },
    {
      field: 'sortOrder',
      type: 'number',
      headerName: 'SORT ORDER',
      flex: 1,
      width: 120,
      editable: true,
      sortable: true,
    },
    {
      field: 'dragOrder',
      headerName: 'SORT ORDER',
      width: 120,
      editable: false,
      sortable: false,
      filterable: false,
      disableColumnMenu: true,
      renderCell: DragHandleCell,
    },
    {
      field: 'actions',
      type: 'actions',
      sortable: false,
      width: 80,
      getActions: (params) => {
        return [
          <GridActionsCellItem
            key="1"
            icon={<DeleteIcon />}
            label="Delete"
            onClick={deleteRow(params.id.toString())}
            disabled={savingProgress.isLoading}
          />,
        ];
      },
    },
  ];

  return (
    <>
      <Typography variant="h5" style={{ paddingBottom: '10px', paddingTop: '10px' }}>
        View and update AssetClass sort order.
      </Typography>
      <Paper elevation={3}>
        <StyledBox>
          <DataGridPro
            editMode="row"
            onRowOrderChange={resetDataRows}
            apiRef={apiRef}
            rows={savingProgress.isLoading ? [] : dataRows}
            columns={columns}
            columnVisibilityModel={columnVisibilityModel}
            onColumnVisibilityModelChange={(model) => {
              setColumnVisibilityModel(model);
            }}
            pageSize={assetClassOrderItems.length}
            disableColumnMenu
            autoHeight
            components={{
              LoadingOverlay: LinearProgress,
              Toolbar: () => {
                validateSortOrderAllocations();

                return (
                  <GridToolbarContainer>
                    <Stack direction="row" alignItems="center">
                      {savingProgress.isLoading && (
                        <div className="LoadingIndicator" style={{ padding: '7px' }}>
                          <PulseLoader size="9px" margin="5px" color={theme.palette.grey[400]} />
                        </div>
                      )}
                      {!hideEditControls && editMode && <Typography color={'error'}>{sortOrderAllocationValidationMessage}</Typography>}
                      {!hideEditControls && editMode && !savingProgress.isLoading && (
                        <IconButton disableFocusRipple disableRipple data-testid="addButton" onClick={handleAddClick} color={'primary'}>
                          <AddIcon style={{ height: '24px' }} />
                        </IconButton>
                      )}
                      {!hideEditControls && !editMode && (
                        <IconButton disableFocusRipple disableRipple data-testid="editButton" onClick={handleEditClick} color={'primary'}>
                          <EditIcon style={{ height: '24px' }} />
                        </IconButton>
                      )}
                      {!hideEditControls && editMode && !savingProgress.isLoading && (
                        <IconButton disableFocusRipple disableRipple data-testid="cancelButton" onClick={handleCancelClick} color={'primary'}>
                          <CancelIcon style={{ height: '24px' }} />
                        </IconButton>
                      )}
                      {!hideEditControls && editMode && !savingProgress.isLoading && (
                        <IconButton
                          disableFocusRipple
                          disableRipple
                          data-testid="saveButton"
                          onClick={handleSave}
                          color={'primary'}
                          disabled={sortOrderAllocationValidationMessage !== ''}
                        >
                          <SaveIcon style={{ height: '24px' }} />
                        </IconButton>
                      )}
                    </Stack>
                  </GridToolbarContainer>
                );
              },
            }}
            loading={itemLoadingProgress.isLoading || savingProgress.isLoading}
            onRowEditStop={(_params: GridRowParams, event: MuiEvent<MuiBaseEvent>) => {
              // we don't want to end editing unless they click save
              event.defaultMuiPrevented = true;
            }}
            onRowEditStart={(_params: GridRowParams, event: MuiEvent<MuiBaseEvent>) => {
              // we don't want to end editing unless they click edit
              event.defaultMuiPrevented = true;
            }}
          />
        </StyledBox>
      </Paper>
    </>
  );
};
